/**
 * LoginPage - blocks access until authenticated
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Lock, WifiOff } from 'lucide-react';
import { isDesktopApp, isDesktopOffline, refreshDesktopConnectivity } from '@/api/desktopSync';
import DesktopUpdateBar from '@/components/DesktopUpdateBar';

const CONNECTION_SETTLE_TIMEOUT_MS = 3000;

const LoginPage = () => {
  const { login, loginOffline } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const mounted = useRef(false);
  const activeAttempt = useRef(null);
  const [connectionSettling, setConnectionSettling] = useState(() => isDesktopApp());
  const [offline, setOffline] = useState(() => isDesktopOffline());
  const [localRuntime, setLocalRuntime] = useState(
    () => window.lvbDesktop?.localRuntimeStatus || null,
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeAttempt.current = null;
    };
  }, []);

  useEffect(() => {
    const desktop = window.lvbDesktop;
    if (typeof desktop?.onLocalRuntimeStatusChanged !== 'function') return undefined;
    return desktop.onLocalRuntimeStatusChanged(setLocalRuntime);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    if (!isDesktopApp()) {
      setConnectionSettling(false);
      setOffline(false);
      return () => {};
    }

    setConnectionSettling(true);
    setOffline(isDesktopOffline());

    timeoutId = setTimeout(() => {
      if (!cancelled) {
        setOffline(isDesktopOffline());
        setConnectionSettling(false);
      }
    }, CONNECTION_SETTLE_TIMEOUT_MS);

    refreshDesktopConnectivity()
      .then((status) => {
        if (cancelled) return;
        if (!status.changed && timeoutId) clearTimeout(timeoutId);
        setOffline(Boolean(status.offline));
        // If Electron changed backend target it will reload the renderer. Keep
        // the login form neutral until that reload happens.
        setConnectionSettling(Boolean(status.changed));
      })
      .catch(() => {
        if (cancelled) return;
        if (timeoutId) clearTimeout(timeoutId);
        setOffline(isDesktopOffline());
        setConnectionSettling(false);
      });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const localRuntimeUnavailable = (
    offline
    && ['starting', 'recovering', 'unavailable', 'rebuild_required'].includes(localRuntime?.state)
  );
  const cannotSubmit = connectionSettling || loading || localRuntimeUnavailable || !username || !password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mounted.current || activeAttempt.current || cannotSubmit) return;
    // The form owns feedback only; AuthContext still owns identity acceptance.
    const attempt = {};
    activeAttempt.current = attempt;
    const ownsAttempt = () => mounted.current && activeAttempt.current === attempt;
    const authenticate = offline ? loginOffline : login;
    setError('');
    setLoading(true);
    try {
      await authenticate(username, password);
    } catch (err) {
      if (ownsAttempt()) setError(err?.message || 'Sign-in failed. Please try again.');
    } finally {
      if (ownsAttempt()) {
        activeAttempt.current = null;
        setLoading(false);
      }
    }
  };

  const editCredential = (setValue, value) => {
    if (activeAttempt.current) return;
    setValue(value);
    setError('');
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="login-page">
      <DesktopUpdateBar />
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900">LVB Workspace</h1>
          <p className="text-sm text-stone-400 mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} aria-busy={loading} className="bg-white border border-stone-200 rounded-lg p-6 shadow-sm">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-username">Username</Label>
              <Input
                id="login-username"
                value={username}
                onChange={(e) => editCredential(setUsername, e.target.value)}
                disabled={loading}
                placeholder="Enter username"
                autoComplete="username"
                autoFocus
                data-testid="login-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => editCredential(setPassword, e.target.value)}
                disabled={loading}
                placeholder="Enter password"
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>
            {!connectionSettling && offline && localRuntimeUnavailable && (
              <div
                className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
                data-testid="login-local-runtime-unavailable"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {['starting', 'recovering'].includes(localRuntime?.state)
                  ? 'Offline services are still preparing on this computer. Sign-in will become available when recovery finishes.'
                  : 'Offline services are unavailable on this computer. Return online to continue; an administrator can review recovery details in Settings.'}
              </div>
            )}
            {!connectionSettling && offline && !localRuntimeUnavailable && (
              <div
                className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800"
                data-testid="login-offline-notice"
              >
                <WifiOff className="h-4 w-4 flex-shrink-0" />
                Cloud sign-in is unavailable. Approved admins may sign in on this desktop for offline work.
              </div>
            )}
            {connectionSettling && (
              <div
                className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-sm text-stone-500"
                data-testid="login-connection-settling"
              >
                Checking connection...
              </div>
            )}
            {error && (
              <p className="text-sm text-red-600 font-medium" data-testid="login-error">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={cannotSubmit}
              data-testid="login-submit-btn"
            >
              {connectionSettling ? 'Checking connection...' : (loading ? 'Signing in...' : (offline ? 'Sign In Offline' : 'Sign In'))}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
